import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import DropdownSelect from '../Generic/DropdownSelect';
import { logInfo } from '../../config_and_helpers/logger';
import { getNodePath, listMoveDestinations } from '../../utils/storageTree';

const LOG_CONTEXT = 'StorageMoveForm';

/**
 * Searchable list of places this node can move to. Unusable rows stay visible
 * and cannot be chosen. The menu is portaled, so the map does not clip it.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode} props.node
 * @param {Array} props.nodes
 * @param {Function} props.onSave - (parentId) => boolean|void|Promise. Return false to keep the form open.
 * @param {Function} props.onCancel
 */
const StorageMoveForm = ({ node, nodes = [], onSave, onCancel }) => {
  const destinations = listMoveDestinations(nodes, node);
  const options = useMemo(
    () =>
      destinations.map(item => {
        const label =
          getNodePath(nodes, item.node.id).map(place => place.name).join(' / ') ||
          item.node.name;
        const note =
          item.reason === 'current'
            ? 'Current'
            : item.reason === 'name'
              ? 'Name already used'
              : item.reason === 'no-place'
                ? 'Nothing here can hold this'
                : '';
        return {
          value: String(item.node.id),
          label: note ? `${label} (${note})` : label,
          selectable: item.selectable,
        };
      }),
    [destinations, nodes]
  );
  const disabledOptions = options.filter(option => !option.selectable).map(option => option.value);
  const firstSelectable = options.find(option => option.selectable);
  const [parentId, setParentId] = useState(firstSelectable ? firstSelectable.value : '');
  const selected = options.find(option => option.value === parentId) || null;

  const handleSubmit = async event => {
    event.preventDefault();
    if (!parentId || disabledOptions.includes(parentId)) return;
    logInfo(LOG_CONTEXT, 'move destination chosen', {
      nodeId: node?.id ?? null,
      parentId,
    });
    const ok = await onSave?.(parentId);
    if (ok === false) return;
  };

  return (
    <Form
      className="storage-node-add nodrag nopan nowheel"
      onSubmit={handleSubmit}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
    >
      {options.length ? (
        <DropdownSelect
          optionsList={options}
          label="Move to"
          placeholder="Search a place"
          selectedValue={selected}
          disabledOptions={disabledOptions}
          setSelectedValue={next => {
            const nextId = next?.value ? String(next.value) : '';
            if (nextId && disabledOptions.includes(nextId)) return;
            setParentId(nextId);
          }}
        />
      ) : (
        <p className="small mb-1">No other place can hold this.</p>
      )}
      <div className="d-flex gap-2 mt-2">
        <Button type="submit" size="sm" className="glass-primary-btn" disabled={!parentId}>
          Save
        </Button>
        <Button type="button" size="sm" variant="link" className="p-0" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default StorageMoveForm;
